import { Component, computed, DestroyRef, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatSlider, MatSliderRangeThumb } from '@angular/material/slider';
import { isNotNullish, isNullish } from '@examdojo/core/util/nullish';
import { distinctUntilChanged, merge, tap } from 'rxjs';

export type ListViewExternalRangeFilterValue = [number, number];

export type RangeFilterFormCtrl = FormControl<ListViewExternalRangeFilterValue | null>;

export function createRangeFilterFormCtrl(): RangeFilterFormCtrl {
  return new FormControl(null);
}

@Component({
  selector: 'dojo-list-view-range-filter',
  template: `
    <h4 class="text-md text-primary mb-2 font-semibold">{{ label() }}</h4>
    @if (subText()) {
      <div class="text-disabled mb-4 text-sm font-medium">{{ subText() }}</div>
    }
    <div class="flex flex-col">
      <div class="-mb-2 flex h-[60px] w-full items-end justify-between px-7">
        @for (histogramBar of histogramData(); track $index) {
          <div
            class="w-[3%] rounded-t-[2px]"
            [style.height.%]="histogramBar.percentage"
            [class]="
              histogramBar.binMin < (rangeFormGroup.controls.max.value ?? this.sliderConfig().max) &&
              histogramBar.binMax > (rangeFormGroup.controls.min.value ?? this.sliderConfig().min)
                ? 'bg-sky-400'
                : 'bg-neutral-100 dark:bg-neutral-200'
            "
          ></div>
        }
      </div>
      <mat-slider discrete [min]="sliderConfig().min" [step]="step()" [max]="sliderConfig().max">
        <input matSliderStartThumb [formControl]="rangeFormGroup.controls.min" />
        <input matSliderEndThumb [formControl]="rangeFormGroup.controls.max" />
      </mat-slider>
      <div class="mt-2 flex justify-between gap-2">
        <div class="flex flex-col gap-1">
          <span class="text-secondary text-sm font-medium">Minimum</span>
          <div class="text-primary flex justify-center">{{ sliderConfig().min }}%</div>
        </div>
        <div>
          <span class="text-secondary text-sm font-medium">Maximum</span>
          <div class="text-primary flex justify-center">{{ sliderConfig().max }}%</div>
        </div>
      </div>
    </div>
  `,
  imports: [MatSlider, ReactiveFormsModule, MatSliderRangeThumb],
})
export class ListViewRangeFilterComponent<Value extends number = number> implements OnInit {
  constructor(private readonly destroyRef: DestroyRef) {}

  readonly formCtrl = input.required<RangeFilterFormCtrl>();
  readonly options = input.required<Value[]>();
  readonly label = input.required<string>();
  readonly subText = input<string>();
  readonly step = input.required<number>();

  readonly rangeFormGroup = new FormGroup({
    min: new FormControl<number | null>(null),
    max: new FormControl<number | null>(null),
  });

  readonly sliderConfig = computed(() => {
    return {
      min: Math.min(...this.options()),
      max: Math.max(...this.options()),
    };
  });

  readonly histogramData = computed(() => {
    return this.getBins(this.options());
  });

  ngOnInit() {
    merge(this.syncParentControlToRangeFormGroup(), this.syncRangeFormGroupToParentControl())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private syncParentControlToRangeFormGroup() {
    const initialFormValue = this.formCtrl().value;

    if (isNotNullish(initialFormValue)) {
      this.rangeFormGroup.setValue(
        {
          min: initialFormValue[0],
          max: initialFormValue[1],
        },
        { emitEvent: false },
      );
    }
    return this.formCtrl().valueChanges.pipe(
      distinctUntilChanged(),
      tap((value) => {
        if (isNullish(value)) {
          this.rangeFormGroup.setValue(
            {
              min: this.sliderConfig().min,
              max: this.sliderConfig().max,
            },
            { emitEvent: false },
          );
        }
      }),
    );
  }

  private syncRangeFormGroupToParentControl() {
    return this.rangeFormGroup.valueChanges.pipe(
      tap((value) => {
        if (isNullish(value.min) && isNullish(value.max)) {
          this.formCtrl().setValue(null);
          return;
        }

        // Apply range filter value if at least one value has been changed by user
        this.formCtrl().setValue([value.min ?? this.sliderConfig().min, value.max ?? this.sliderConfig().max]);
      }),
    );
  }

  private getBins(data: number[]) {
    const binsCount = 26;

    if (data.length === 0) {
      return Array.from({ length: binsCount }, () => ({
        percentage: 0,
        binMin: 0,
        binMax: 0,
      }));
    }

    const minValue = Math.min(...data);
    const maxValue = Math.max(...data);
    const range = maxValue - minValue;

    if (range === 0) {
      return Array.from({ length: binsCount }, (_, i) => ({
        percentage: i === binsCount - 1 ? 100 : 0,
        binMin: minValue,
        binMax: maxValue,
      }));
    }

    const binWidth = range / binsCount;

    const bins = Array.from({ length: binsCount }, (_, i) => {
      const binMin = minValue + i * binWidth;
      const binMax = i === binsCount - 1 ? maxValue : minValue + (i + 1) * binWidth;
      return { binMin, binMax, count: 0 };
    });

    for (const x of data) {
      if (x === maxValue) {
        bins[binsCount - 1].count++;
      } else {
        const index = Math.floor((x - minValue) / binWidth);
        bins[index].count++;
      }
    }

    const maxCount = Math.max(...bins.map((bin) => bin.count));

    const result = bins.map((bin) => ({
      percentage: maxCount > 0 ? Math.floor((bin.count / maxCount) * 100) : 0,
      binMin: bin.binMin,
      binMax: bin.binMax,
    }));

    return result;
  }
}
